import { useState, useEffect, useMemo, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useSupabase } from '@/hooks/useSupabase'
import { showToast } from '@/components/layout/Toast'
import { IconEye, IconEyeOff, IconChevronDown, IconChevronRight, IconEdit } from '@/components/icons/Icons'
import type { Empresa, CanalChat } from '@/lib/types'

interface CanalOrigin {
  canal: CanalChat
  channel_id: string
}

interface ChannelVisibilityConfigProps {
  empresa: Empresa | null
  empresaId: number | undefined
  onConfigChange: (config: Empresa['canales_config']) => void
}

const CANAL_LABELS: Record<string, string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  web_chat: 'Web Chat',
  email: 'Email',
  sms: 'SMS',
  portal: 'Portal',
}

export default function ChannelVisibilityConfig({ empresa, empresaId, onConfigChange }: ChannelVisibilityConfigProps) {
  const db = useSupabase()
  const [origins, setOrigins] = useState<CanalOrigin[]>([])
  const [loadingOrigins, setLoadingOrigins] = useState(true)
  const [expandedCanals, setExpandedCanals] = useState<Set<string>>(new Set())
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const editInputRef = useRef<HTMLInputElement>(null)

  const currentConfig = useMemo(() => {
    return empresa?.canales_config || {}
  }, [empresa?.canales_config])

  function getDisplayName(canal: string, channelId: string): string {
    const cfg = currentConfig[canal]
    if (cfg?.origins?.[channelId]?.nombre) return cfg.origins[channelId].nombre!
    return channelId === 'default' ? 'General' : channelId
  }

  // Load all conversations to extract unique (canal, channel_id) pairs
  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoadingOrigins(true)
      try {
        const { data } = await supabase.from('conversaciones').select('canal, channel_id')
        if (cancelled || !data) return
        const seen = new Set<string>()
        const unique: CanalOrigin[] = []
        for (const row of data) {
          const key = `${row.canal}:${row.channel_id || 'default'}`
          if (seen.has(key)) continue
          seen.add(key)
          unique.push({
            canal: row.canal as CanalChat,
            channel_id: (row.channel_id as string) || 'default',
          })
        }
        setOrigins(unique)
        // Auto-expand canals that have visibility config
        const config = currentConfig
        const canalsWithConfig = Object.keys(config).filter(k => !config[k]?.visible)
        setExpandedCanals(new Set(canalsWithConfig))
      } catch {
        // non-critical
      } finally {
        if (!cancelled) setLoadingOrigins(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const startEdit = (canal: string, channelId: string) => {
    const key = `${canal}:${channelId}`
    setEditingKey(key)
    setEditValue(getDisplayName(canal, channelId))
    requestAnimationFrame(() => editInputRef.current?.focus())
  }

  const commitEdit = (canal: string, channelId: string) => {
    if (editingKey !== `${canal}:${channelId}`) return
    setEditingKey(null)
    const newName = editValue.trim()
    if (!newName || newName === getDisplayName(canal, channelId)) return
    const config = { ...currentConfig }
    if (!config[canal]) {
      config[canal] = { visible: true, origins: {} }
    }
    config[canal] = {
      ...config[canal],
      origins: {
        ...config[canal].origins,
        [channelId]: {
          ...config[canal].origins[channelId],
          nombre: newName,
          visible: config[canal].origins[channelId]?.visible ?? true,
        },
      },
    }
    onConfigChange(config)
  }

  const cancelEdit = () => {
    setEditingKey(null)
    setEditValue('')
  }

  // Group origins by canal
  const grouped = useMemo(() => {
    const map = new Map<string, CanalOrigin[]>()
    for (const o of origins) {
      const existing = map.get(o.canal) || []
      existing.push(o)
      map.set(o.canal, existing)
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [origins])

  const canalVisible = (canal: string): boolean => {
    const cfg = currentConfig[canal]
    return cfg === undefined || cfg.visible
  }

  const originVisible = (canal: string, channelId: string): boolean => {
    const cfg = currentConfig[canal]
    if (cfg === undefined) return true
    if (!cfg.visible) return false
    const originCfg = cfg.origins?.[channelId]
    return originCfg === undefined || originCfg.visible
  }

  const setCanalVisible = (canal: string, visible: boolean) => {
    const config = { ...currentConfig }
    if (!config[canal]) {
      config[canal] = { visible, origins: {} }
    } else {
      config[canal] = { ...config[canal], visible }
    }
    onConfigChange(config)
  }

  const setOriginVisible = (canal: string, channelId: string, visible: boolean) => {
    const config = { ...currentConfig }
    if (!config[canal]) {
      config[canal] = { visible: true, origins: {} }
    }
    config[canal] = {
      ...config[canal],
      origins: {
        ...config[canal].origins,
        [channelId]: { ...config[canal].origins[channelId], visible, nombre: config[canal].origins[channelId]?.nombre || channelId },
      },
    }
    onConfigChange(config)
  }

  const toggleExpand = (canal: string) => {
    setExpandedCanals(prev => {
      const next = new Set(prev)
      if (next.has(canal)) next.delete(canal)
      else next.add(canal)
      return next
    })
  }

  const handleSave = async () => {
    if (!empresaId) return
    try {
      await db.update('empresa', {
        id: empresaId,
        canales_config: currentConfig,
      } as Record<string, unknown>)
      showToast('Configuracion de canales guardada', 'success')
    } catch {
      showToast('Error al guardar configuracion de canales', 'error')
    }
  }

  const hiddenCount = useMemo(() => {
    let count = 0
    for (const [canal, origins] of grouped) {
      if (!canalVisible(canal)) { count++; continue }
      for (const o of origins) {
        if (!originVisible(canal, o.channel_id)) count++
      }
    }
    return count
  }, [grouped, currentConfig])

  if (loadingOrigins) {
    return (
      <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-secondary)' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
        Cargando canales...
      </div>
    )
  }

  if (origins.length === 0) {
    return (
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header"><h2>Canales de Conversacion</h2></div>
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
          No hay conversaciones con canales detectados. Los canales apareceran automaticamente cuando el sistema reciba mensajes.
        </div>
      </div>
    )
  }

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="card-header">
        <h2>Canales de Conversacion</h2>
        {hiddenCount > 0 && (
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {hiddenCount} oculto{hiddenCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div style={{ padding: '8px 0' }}>
        {grouped.map(([canal, canalOrigins]) => {
          const expanded = expandedCanals.has(canal)
          const visible = canalVisible(canal)
          return (
            <div key={canal} style={{ borderBottom: '1px solid var(--border)', padding: '4px 0' }}>
              {/* Canal row */}
              <div className="channel-vis-row channel-vis-row--canal">
                <button
                  type="button"
                  className="channel-vis-expand"
                  onClick={() => toggleExpand(canal)}
                  aria-label={expanded ? 'Contraer' : 'Expandir'}
                >
                  {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                </button>
                <span className="channel-vis-label">{CANAL_LABELS[canal] || canal}</span>
                <span className="channel-vis-count">{canalOrigins.length} origen{canalOrigins.length !== 1 ? 'es' : ''}</span>
                <button
                  type="button"
                  className={`channel-vis-toggle ${visible ? 'channel-vis-toggle--on' : 'channel-vis-toggle--off'}`}
                  onClick={() => setCanalVisible(canal, !visible)}
                  title={visible ? 'Ocultar canal' : 'Mostrar canal'}
                >
                  {visible ? <IconEye size={14} /> : <IconEyeOff size={14} />}
                  <span>{visible ? 'Visible' : 'Oculto'}</span>
                </button>
              </div>

              {/* Origins (collapsible) */}
              {expanded && (
                <div style={{ paddingLeft: 32 }}>
                  {canalOrigins.map((o) => {
                    const oVisible = originVisible(canal, o.channel_id)
                    const editKey = `${canal}:${o.channel_id}`
                    const isEditing = editingKey === editKey
                    const displayName = getDisplayName(canal, o.channel_id)
                    return (
                      <div key={o.channel_id} className="channel-vis-row channel-vis-row--origin">
                        {isEditing ? (
                          <input
                            ref={editInputRef}
                            className="channel-vis-input"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={() => commitEdit(canal, o.channel_id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitEdit(canal, o.channel_id)
                              if (e.key === 'Escape') cancelEdit()
                            }}
                          />
                        ) : (
                          <span
                            className="channel-vis-label channel-vis-label--origin"
                            onClick={() => startEdit(canal, o.channel_id)}
                            title="Click para editar nombre"
                          >
                            {displayName}
                            <IconEdit size={10} style={{ marginLeft: 6, opacity: 0.4, verticalAlign: 'middle' }} />
                          </span>
                        )}
                        <button
                          type="button"
                          className={`channel-vis-toggle ${oVisible ? 'channel-vis-toggle--on' : 'channel-vis-toggle--off'}`}
                          onClick={() => setOriginVisible(canal, o.channel_id, !oVisible)}
                          title={oVisible ? 'Ocultar origen' : 'Mostrar origen'}
                        >
                          {oVisible ? <IconEye size={12} /> : <IconEyeOff size={12} />}
                          <span>{oVisible ? 'Visible' : 'Oculto'}</span>
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-primary btn-sm" onClick={handleSave} type="button">
          Guardar Configuracion
        </button>
      </div>
    </div>
  )
}
